import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

export default function ThankYou() {
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  // Noindex — confirmation page should not be crawled
  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow';
    meta.id = 'thankyou-robots-meta';
    document.head.appendChild(meta);
    return () => {
      const existing = document.getElementById('thankyou-robots-meta');
      if (existing) existing.remove();
    };
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 80);
    return () => clearTimeout(timer);
  }, []);

  const expectations = [
    {
      icon: "ri-search-eye-line",
      title: "Your online presence — reviewed",
      desc: "Before we talk, I'll personally audit your website, Google profile, and social media to see where clients are slipping through.",
    },
    {
      icon: "ri-roadmap-line",
      title: "A clear plan, built for your spa",
      desc: "No generic advice. You'll leave with a specific, actionable roadmap tailored to what your med spa is missing right now.",
    },
    {
      icon: "ri-question-answer-line",
      title: "Ask me anything",
      desc: "Pricing, strategy, platforms — bring your biggest questions. This call is yours, use it fully.",
    },
    {
      icon: "ri-shield-check-line",
      title: "Zero pressure, zero pitch",
      desc: "This is a free audit. The value is the clarity you walk away with, nothing more.",
    },
  ];

  return (
    <main
      className="min-h-screen"
      style={{ background: "#0A0A0A", fontFamily: "'Inter', sans-serif" }}
    >
      {/* Header */}
      <header
        className="w-full flex items-center justify-between px-8"
        style={{
          height: "72px",
          borderBottom: "1px solid rgba(201, 149, 111, 0.15)",
        }}
      >
        <button
          onClick={() => navigate("/")}
          className="flex items-center h-full py-2.5 cursor-pointer"
        >
          <img
            src="https://static.readdy.ai/image/b1af610ebea9d5dff7bba4d7db421a2d/efa59382ce49994f108d3b6dc4f24e13.png"
            alt="Capital Growth Studio"
            className="h-full object-contain"
            style={{ maxWidth: "180px" }}
          />
        </button>
        <button
          onClick={() => navigate("/")}
          className="whitespace-nowrap flex items-center gap-2 text-sm font-medium cursor-pointer transition-all duration-300 hover:opacity-70"
          style={{ color: "rgba(255,255,255,0.55)", fontFamily: "'Inter', sans-serif" }}
        >
          <i className="ri-arrow-left-line text-sm" />
          Back to home
        </button>
      </header>

      {/* Main Content */}
      <div
        className="flex items-center justify-center"
        style={{ minHeight: "calc(100vh - 72px - 65px)" }}
      >
        <div
          ref={contentRef}
          className="max-w-3xl w-full mx-auto px-6 py-16"
          style={{
            opacity: visible ? 1 : 0,
            transform: visible ? "translateY(0)" : "translateY(28px)",
            transition: "opacity 0.85s ease, transform 0.85s ease",
          }}
        >
          {/* Confirmation badge */}
          <div className="flex flex-col items-center text-center mb-16">
            <a href="https://" target="_blank" rel="noopener noreferrer">
              <div
                className="w-20 h-20 flex items-center justify-center rounded-full mb-8 overflow-hidden"
                style={{
                  background: "rgba(201, 149, 111, 0.12)",
                  border: "2px solid #C9956F",
                  boxShadow: "0 0 40px rgba(201, 149, 111, 0.15)",
                }}
              >
                <img
                  src="https://static.readdy.ai/image/b1af610ebea9d5dff7bba4d7db421a2d/efa59382ce49994f108d3b6dc4f24e13.png"
                  alt="Capital Growth Studio"
                  className="w-full h-full object-contain p-2"
                />
              </div>
            </a>

            <span
              className="inline-block text-xs font-semibold tracking-widest uppercase mb-5 px-4 py-1.5 rounded-full"
              style={{
                background: "rgba(201, 149, 111, 0.12)",
                color: "#C9956F",
                border: "1px solid rgba(201, 149, 111, 0.3)",
              }}
            >
              Booking Confirmed
            </span>

            <h1
              className="text-4xl md:text-5xl font-bold text-white mb-5 leading-tight"
              style={{ fontFamily: "'Playfair Display', serif", letterSpacing: "-0.02em" }}
            >
              You&apos;re on the calendar!
            </h1>

            <p
              className="text-lg max-w-xl leading-relaxed"
              style={{ color: "rgba(255,255,255,0.55)" }}
            >
              Your free 15-minute growth audit is booked. Check your inbox.&nbsp;A calendar invite with the call link is on its way. Check spam just in case.
            </p>
          </div>

          {/* Divider */}
          <div
            className="w-full mb-14"
            style={{ height: "1px", background: "rgba(201, 149, 111, 0.15)" }}
          />

          {/* Personal message from Guilherme */}
          <div
            className="rounded-lg p-8 md:p-10 relative overflow-hidden"
            style={{
              background: "#141414",
              border: "1px solid rgba(201, 149, 111, 0.25)",
            }}
          >
            {/* Decorative quote mark */}
            <div
              className="absolute top-6 right-8 text-7xl font-serif leading-none select-none pointer-events-none"
              style={{ color: "rgba(201, 149, 111, 0.08)", fontFamily: "Georgia, serif" }}
            >
              &ldquo;
            </div>

            <div className="flex items-start gap-5 relative z-10">
              {/* Headshot */}
              <div
                className="w-14 h-14 flex-shrink-0 rounded-full overflow-hidden"
                style={{
                  border: "2px solid rgba(201, 149, 111, 0.5)",
                }}
              >
                <img
                  src="https://storage.readdy-site.link/project_files/176b8c5d-519b-49f5-ae57-58286d1a3153/0e8ddf8b-1d81-4845-bf97-2b4907fc3b99_Heahshot-LP.png?v=c1738d7937f59ec8691b58943b306c65"
                  alt="Guilherme Oliveira"
                  className="w-full h-full object-cover object-top"
                />
              </div>

              <div className="flex-1">
                <p
                  className="text-base md:text-lg leading-relaxed mb-5 italic"
                  style={{
                    color: "rgba(255,255,255,0.75)",
                    fontFamily: "'Playfair Display', serif",
                    letterSpacing: "0.01em",
                  }}
                >
                  &ldquo;Work and deliver on top of your capabilities, let the world judge and tell you the truth. Then improve. Fixed individuals gonna sink in their self-pity and lose the little power they have.&rdquo;
                </p>
                <div>
                  <p className="text-sm font-semibold text-white" style={{ fontFamily: "'Inter', sans-serif" }}>
                    Guilherme Oliveira
                  </p>
                  <p
                    className="text-xs mt-0.5"
                    style={{ color: "rgba(255,255,255,0.35)", fontFamily: "'Inter', sans-serif" }}
                  >
                    Founder, Capital Growth Studio &nbsp;·&nbsp; Manhattan, NYC
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Footer micro-text */}
          <p
            className="text-center text-xs mt-10"
            style={{ color: "rgba(255,255,255,0.25)", fontFamily: "'Inter', sans-serif" }}
          >
            Can&apos;t make it? Use the calendar link in your confirmation email to reschedule anytime.
          </p>
        </div>
      </div>

      {/* Footer */}
      <footer
        className="py-8 px-6 text-center"
        style={{ borderTop: "1px solid rgba(201, 149, 111, 0.1)" }}
      >
        <p
          className="text-xs"
          style={{ color: "rgba(255,255,255,0.2)", fontFamily: "'Inter', sans-serif" }}
        >
          &copy; {new Date().getFullYear()} Capital Growth Studio. All rights reserved.
        </p>
      </footer>
    </main>
  );
}
